import { Appointment, AppointmentEvent, AppointmentOutcomeEnum } from '../../../types';

export type SeriesTimelineRowVariant = 'completed' | 'cancelled' | 'needsOutcome';

export type SeriesTimelineRow = {
  event: Appointment;
  variant: SeriesTimelineRowVariant;
  isFirstConsultation: boolean;
  followUpSeq: number | null;
  isPast: boolean;
};

function getEventOutcome(event: Appointment): AppointmentOutcomeEnum | undefined {
  const eventTypeData = event.eventTypeData as AppointmentEvent;
  return eventTypeData.outcome;
}

function eventDateTimeToMs(event: Appointment): number {
  const dayMs = new Date(event.date).getTime();
  const minutesMs = event.time * 60 * 1000;
  return dayMs + minutesMs;
}

// Variant is driven by `eventTypeData.outcome` only. event.status is
// intentionally ignored — staff is responsible for recording an outcome
// on every event the BE returns, regardless of status.
function resolveRowVariant(outcome: AppointmentOutcomeEnum | undefined): SeriesTimelineRowVariant {
  if (outcome === AppointmentOutcomeEnum.COMPLETED) {
    return 'completed';
  }
  if (outcome === AppointmentOutcomeEnum.CANCELLED) {
    return 'cancelled';
  }
  return 'needsOutcome';
}

export type BuildSeriesTimelineRowsArgs = {
  events: Appointment[];
  rootEventId: string | undefined;
  nowMs: number;
};

export function buildSeriesTimelineRows(args: BuildSeriesTimelineRowsArgs): SeriesTimelineRow[] {
  const { events, rootEventId, nowMs } = args;

  const sorted = events.slice().sort((a, b) => eventDateTimeToMs(a) - eventDateTimeToMs(b));

  const rows: SeriesTimelineRow[] = [];
  let followUpSeq = 0;

  for (let eventsIndex = 0; eventsIndex < sorted.length; eventsIndex += 1) {
    const event = sorted[eventsIndex];

    const isFirstConsultation = rootEventId !== undefined && event.id === rootEventId;
    const eventDateTimeMs = eventDateTimeToMs(event);
    const isPast = eventDateTimeMs <= nowMs;
    const outcome = getEventOutcome(event);

    const variant = resolveRowVariant(outcome);

    let seq: number | null = null;
    if (!isFirstConsultation && variant !== 'cancelled') {
      followUpSeq += 1;
      seq = followUpSeq;
    }

    rows.push({ event, variant, isFirstConsultation, followUpSeq: seq, isPast });
  }

  return rows;
}
